import { useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Select } from 'antd';
import { DeleteOutlined, PlusOutlined, SendOutlined } from '@ant-design/icons';
import { submitParties } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import type { PartiesSpec, Party, PartyRole, Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

interface Row {
  role: PartyRole;
  fullName: string;
  din: string;
  ownershipPct: number | null;
  nationality: string;
  email: string;
}

/**
 * PARTIES task (OL-22): the applicant declares related parties — directors, beneficial owners,
 * officers, contacts — within the spec's roles and bounds. The room checks what it can (count,
 * ownership where required, total ≤ 100%); the server re-checks everything and is authoritative.
 * These are DECLARED facts: the room never looks anyone up and never shows a result.
 */
export function PartiesTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const spec = (task.spec as PartiesSpec | undefined) ?? { roles: ['DIRECTOR'] as PartyRole[] };
  const min = spec.minParties ?? 1;
  const max = spec.maxParties ?? 20;
  const owned = new Set(spec.requireOwnershipFor ?? []);
  const needsDin = new Set(spec.requireDinFor ?? []);
  const blank = (): Row => ({
    role: spec.roles[0],
    fullName: '',
    din: '',
    ownershipPct: null,
    nationality: '',
    email: '',
  });
  const [rows, setRows] = useState<Row[]>(() => Array.from({ length: min }, blank));
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const total = useMemo(
    () => rows.reduce((sum, r) => sum + (r.ownershipPct ?? 0), 0),
    [rows],
  );

  if (task.status !== 'PENDING' || done) {
    return <Alert type="success" showIcon message={t('parties.submitted')} />;
  }

  const update = (i: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const missingOwnership = rows.some((r) => owned.has(r.role) && r.ownershipPct == null);
  const missingName = rows.some((r) => r.fullName.trim() === '');
  const badDin = rows.some((r) => needsDin.has(r.role) && !/^\d{8}$/.test(r.din.trim()));
  const overHundred = total > 100;
  const countOk = rows.length >= min && rows.length <= max;
  const canSubmit = countOk && !missingName && !badDin && !missingOwnership && !overHundred && !busy;

  async function send() {
    setBusy(true);
    setError(null);
    const parties: Party[] = rows.map((r) => ({
      role: r.role,
      fullName: r.fullName.trim(),
      ...(needsDin.has(r.role) && r.din.trim() ? { din: r.din.trim() } : {}),
      ...(r.ownershipPct != null ? { ownershipPct: r.ownershipPct } : {}),
      ...(r.nationality.trim() ? { nationality: r.nationality.trim().toUpperCase() } : {}),
      ...(r.email.trim() ? { email: r.email.trim() } : {}),
    }));
    try {
      const res = await submitParties(task.taskId, parties);
      onStatus(res.status);
      setDone(true);
    } catch (err) {
      setError(
        normalizeError(err).kind === 'INVALID'
          ? t('parties.countRange', { min, max })
          : t('error.inline'),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dr-stack">
      <div className="dr-meta">{text('intro', 'parties.intro', { min, max })}</div>
      {rows.map((r, i) => (
        <fieldset key={i} className="dr-party" aria-label={t('parties.personN', { n: i + 1 })}>
          <legend className="dr-party__legend">{t('parties.personN', { n: i + 1 })}</legend>
          <label className="dr-field">
            <span>{t('parties.role')}</span>
            <Select
              value={r.role}
              onChange={(role: PartyRole) => update(i, { role })}
              options={spec.roles.map((role) => ({ value: role, label: t(`parties.role_${role}`) }))}
            />
          </label>
          <label className="dr-field">
            <span>{t('parties.fullName')}</span>
            <Input
              value={r.fullName}
              maxLength={200}
              onChange={(e) => update(i, { fullName: e.target.value })}
            />
          </label>
          {needsDin.has(r.role) && (
            <label className="dr-field">
              <span>{t('parties.din')}</span>
              <Input
                value={r.din}
                maxLength={8}
                inputMode="numeric"
                data-testid="party-din"
                onChange={(e) => update(i, { din: e.target.value })}
              />
            </label>
          )}
          <label className="dr-field">
            <span>{t('parties.ownership')}</span>
            <InputNumber
              min={0}
              max={100}
              step={0.01}
              precision={2}
              value={r.ownershipPct}
              onChange={(v) => update(i, { ownershipPct: v ?? null })}
              status={owned.has(r.role) && r.ownershipPct == null ? 'warning' : undefined}
            />
            {owned.has(r.role) && r.ownershipPct == null && (
              <span className="dr-meta">{t('parties.ownershipRequired')}</span>
            )}
          </label>
          <label className="dr-field">
            <span>{t('parties.nationality')}</span>
            <Input
              value={r.nationality}
              maxLength={2}
              onChange={(e) => update(i, { nationality: e.target.value })}
            />
          </label>
          <label className="dr-field">
            <span>{t('parties.email')}</span>
            <Input
              type="email"
              value={r.email}
              onChange={(e) => update(i, { email: e.target.value })}
            />
          </label>
          {rows.length > min && (
            <Button
              icon={<DeleteOutlined />}
              onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
            >
              {t('parties.remove')}
            </Button>
          )}
        </fieldset>
      ))}
      {rows.length < max && (
        <Button icon={<PlusOutlined />} onClick={() => setRows((prev) => [...prev, blank()])}>
          {text('add', 'parties.add')}
        </Button>
      )}
      <div className="dr-meta" aria-live="polite">
        {t('parties.total', { pct: Number(total.toFixed(2)) })}
      </div>
      {overHundred && <Alert type="warning" showIcon message={t('parties.overHundred')} />}
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        type="primary"
        size="large"
        block
        disabled={!canSubmit}
        loading={busy}
        icon={<SendOutlined />}
        onClick={send}
      >
        {busy ? t('parties.submitting') : text('submit', 'parties.submit')}
      </Button>
    </div>
  );
}
